/**
 * 필터 — 좌 레일 3번 구획 (DESIGN §8.6.1 · §8.6.8).
 *
 * **레일이 길어지지 않는 이유가 이 파일에 있다.** v0.3 이 좌 레일을 폐기한
 * 원인은 "선택지를 펼치면 레일이 무한정 길어진다" 였다 — 실측 선택지가
 * 사업자 146 · 제조사 44 · 시군구 다수다. 여기서는 레일이 **트리거만** 갖고
 * 목록은 `position: fixed` 플라이아웃으로 **레일 밖에** 뜬다. 레일의 세로
 * 길이가 선택지 수와 완전히 무관해진다.
 *
 * 적용된 것은 항상 보인다 — **무엇이 걸려 있는지 모른 채 숫자를 보는 일이
 * 없어야 한다.** 다만 칩도 레일을 늘리므로 3줄을 넘으면 접고, 접혀도 개수는
 * 트리거 배지에 남는다.
 *
 * **적용되지 않는다는 것도 여기서 말한다** (§8.6.10). 9개 화면 중 5개는 어떤
 * 축도 받지 않는데, 그 화면에서도 `사업자 GS차지비 ✕` 칩이 똑같이 박혀 있었다.
 * 걸어 둔 표시는 보이는데 숫자는 전국 값이니 사용자가 내리는 결론은 "필터가
 * 고장났다"다 — 실제로 그 보고를 받았다. 레일 맨 아래의 「이 화면의 범위」로는
 * 부족했다. 720px 화면에서 접혀 보이지 않았고, **읽히지 않는 설명은 없는
 * 설명이다.**
 *
 * 그렇다고 **지우거나 비활성화하지 않는다.** 여기서 고른 값은 다른 화면에서
 * 유효하다. 사라지면 그것대로 거짓말이고, 누를 수 없으면 사용자는 자기가 뭘
 * 잘못했는지 묻게 된다 (§8.2.1 레이어 규칙과 같은 문법).
 *
 * 루트 클래스 `.fbar` 와 트리거 마크업은 바꾸지 않는다 — e2e 가 잡고 있다.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { YearFilter } from '@/components/YearFilter'
import { fetchPlain } from '@/lib/api'
import { axisState, partialReason, type ScopeAxis } from '@/lib/pageScope'
import { useFilters } from '@/stores/filters'
import './FilterBar.css'

interface Option {
  code: string
  label: string
  n: number | null
}

interface FilterOptions {
  options: Record<string, Option[]>
  data_version: string
}

type GroupKey = 'busi' | 'zcode' | 'maker' | 'chgerType' | 'kind'

/** `axis` 는 `pageScope` 의 축 이름이다 — 둘이 어긋나면 표시가 거짓이 된다 */
const GROUPS: { key: GroupKey; dim: string; title: string; axis: ScopeAxis }[] = [
  { key: 'busi', dim: 'busi', title: '사업자', axis: 'busi' },
  { key: 'zcode', dim: 'zcode', title: '지역', axis: 'zcode' },
  { key: 'maker', dim: 'maker', title: '제조사', axis: 'maker' },
  { key: 'chgerType', dim: 'chger_type', title: '충전기 타입', axis: 'chgerType' },
  { key: 'kind', dim: 'kind', title: '설치 환경', axis: 'kind' },
]

function compact(n: number): string {
  if (n >= 10000) return `${Math.round(n / 1000)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

/** 플라이아웃 세로 여유 — 뷰포트 밖으로 나가지 않게 위로 밀어 올릴 때 쓴다 */
const FLYOUT_MAX_H = 420

/** 레일에 상시 노출하는 적용 칩 개수. 272px 폭에서 대략 3줄이다 (§8.6.8) */
const CHIP_CAP = 6

interface Props {
  /** 현재 화면. 어느 축이 실제로 걸리는지 판정하는 데 쓴다 (§8.6.10) */
  page: string
}

export function FilterBar({ page }: Props) {
  const filters = useFilters()
  const [openDim, setOpenDim] = useState<string | null>(null)
  const [popTop, setPopTop] = useState(0)
  const [search, setSearch] = useState<Record<string, string>>({})
  const [showAllChips, setShowAllChips] = useState(false)
  const barRef = useRef<HTMLDivElement | null>(null)

  /**
   * 트리거 위치에 맞춰 플라이아웃을 띄운다. 레일이 sticky 라 열려 있는 동안
   * 트리거가 거의 안 움직이므로 열 때 한 번 재면 충분하다. 뷰포트 아래로
   * 넘칠 때만 위로 밀어 올린다.
   */
  const toggleAt = (dim: string, el: HTMLElement) => {
    if (openDim === dim) {
      setOpenDim(null)
      return
    }
    const r = el.getBoundingClientRect()
    const maxTop = Math.max(8, window.innerHeight - FLYOUT_MAX_H - 8)
    setPopTop(Math.min(r.top, maxTop))
    setOpenDim(dim)
  }

  const { data } = useQuery({
    queryKey: ['meta', 'filters'],
    queryFn: ({ signal }) => fetchPlain<FilterOptions>('/meta/filters', signal),
    staleTime: 5 * 60 * 1000,
  })

  // 바깥 클릭·Escape 로 닫는다 — 포커스가 갇히지 않게
  useEffect(() => {
    if (openDim == null) return
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpenDim(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDim(null)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [openDim])

  const labelOf = useMemo(() => {
    const map = new Map<string, string>()
    for (const [dim, opts] of Object.entries(data?.options ?? {})) {
      for (const o of opts) map.set(`${dim}|${o.code}`, o.label)
    }
    return map
  }, [data])

  const applied = GROUPS.flatMap((g) =>
    filters[g.key].map((code) => ({
      group: g,
      code,
      label: labelOf.get(`${g.dim}|${code}`) ?? code,
      state: axisState(page, g.axis),
    })),
  )
  const yearCount =
    filters.year.length + (filters.yearFrom != null ? 1 : 0) + (filters.yearTo != null ? 1 : 0)
  const conditionCount =
    (filters.floor ? 1 : 0) + (filters.minCoverage !== 0.5 ? 1 : 0)
  const activeCount = applied.length + conditionCount + yearCount

  // 「표시 조건」은 층·커버리지 두 축을 묶은 트리거다. 둘의 상태가 다를 수
  // 있으므로 나쁜 쪽(off > partial > on)을 트리거에 보인다 — 좋은 쪽을 보이면
  // 걸리지 않는 축이 걸리는 것처럼 읽힌다.
  const condState = (['floor', 'minCoverage'] as const)
    .map((a) => axisState(page, a))
    .reduce((worst, s) => (worst === 'off' || s === 'off' ? 'off'
      : worst === 'partial' || s === 'partial' ? 'partial' : 'on'), 'on' as const)

  const offAxes = [
    ...GROUPS.map((g) => g.axis), 'year' as const, 'floor' as const, 'minCoverage' as const,
  ].filter((a) => axisState(page, a) === 'off')
  const allOff = offAxes.length === GROUPS.length + 3

  return (
    <div className={`fbar${allOff ? ' fbar--alloff' : ''}`} ref={barRef}>
      {/* 무엇이 안 걸리는지 필터 **위에서** 말한다 — 아래에 두면 칩에 밀려
          접히고, 접힌 설명은 없는 설명이다 (§8.6.10) */}
      {offAxes.length > 0 && (
        <p className="fbar__scope">
          {allOff
            ? '이 화면은 왼쪽 필터를 타지 않습니다'
            : `이 화면은 ${offAxes.length}개 축을 타지 않습니다`}
          <span className="fbar__scopewhere">사유는 아래 「이 화면의 범위」</span>
        </p>
      )}

      <div className="fbar__row">
        <span className="fbar__label">필터</span>

        {GROUPS.map((g) => {
          const all = data?.options[g.dim] ?? []
          const chosen = filters[g.key]
          const isOpen = openDim === g.dim
          const st = axisState(page, g.axis)
          const term = (search[g.dim] ?? '').trim()
          const list = term
            ? all.filter((o) => o.label.includes(term) || o.code.includes(term))
            : all

          return (
            <div className="fbar__item" key={g.dim}>
              <button
                type="button"
                className={`fbar__trigger${chosen.length ? ' is-on' : ''}${
                  st === 'off' ? ' is-off' : st === 'partial' ? ' is-partial' : ''
                }`}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                onClick={(e) => toggleAt(g.dim, e.currentTarget)}
                disabled={all.length === 0}
                title={
                  st === 'off'
                    ? `${g.title} 는 이 화면에 적용되지 않습니다`
                    : st === 'partial'
                      ? partialReason(page, g.axis)
                      : undefined
                }
              >
                {g.title}
                {chosen.length > 0 && <span className="fbar__badge num">{chosen.length}</span>}
                <span className="fbar__caret" aria-hidden="true">
                  ▾
                </span>
              </button>

              {isOpen && (
                <div
                  className="pop"
                  role="dialog"
                  aria-label={`${g.title} 선택`}
                  style={{ top: popTop }}
                >
                  <div className="pop__head">
                    <input
                      type="search"
                      className="pop__search"
                      placeholder={`${g.title} 검색 (${all.length}개)`}
                      value={search[g.dim] ?? ''}
                      onChange={(e) => setSearch((p) => ({ ...p, [g.dim]: e.target.value }))}
                      aria-label={`${g.title} 검색`}
                    />
                    {chosen.length > 0 && (
                      <button
                        type="button"
                        className="btn-text"
                        onClick={() => filters.set({ [g.key]: [] })}
                      >
                        해제
                      </button>
                    )}
                  </div>
                  <ul className="pop__list">
                    {list.slice(0, 300).map((o) => {
                      const on = chosen.includes(o.code)
                      return (
                        <li key={o.code}>
                          <label className={`pop__opt${on ? ' is-on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => filters.toggleList(g.key, o.code)}
                            />
                            <span className="pop__optlabel">{o.label}</span>
                            {o.n != null && <span className="pop__optn num">{compact(o.n)}</span>}
                          </label>
                        </li>
                      )
                    })}
                    {list.length === 0 && <li className="pop__empty">검색 결과가 없습니다.</li>}
                  </ul>
                </div>
              )}
            </div>
          )
        })}

        {/* 설치 연식 — 특정 연식 + 기간 (§8.10.2).
            **표시 조건과 따로 둔다** — 층·커버리지는 "어떻게 볼까"지만
            연식은 "무엇을 볼까"다. 같은 상자에 넣으면 위계가 무너진다 */}
        <div className="fbar__item">
          <button
            type="button"
            className={`fbar__trigger${yearCount ? ' is-on' : ''}${
              axisState(page, 'year') === 'off' ? ' is-off' : ''
            }`}
            aria-expanded={openDim === '_year'}
            aria-haspopup="dialog"
            onClick={(e) => toggleAt('_year', e.currentTarget)}
            title={
              axisState(page, 'year') === 'off'
                ? '설치 연식은 이 화면에 적용되지 않습니다'
                : undefined
            }
          >
            설치 연식
            {yearCount > 0 && <span className="fbar__badge num">{yearCount}</span>}
            <span className="fbar__caret" aria-hidden="true">
              ▾
            </span>
          </button>

          {openDim === '_year' && (
            <div className="pop" role="dialog" aria-label="설치 연식" style={{ top: popTop }}>
              <YearFilter />
            </div>
          )}
        </div>

        {/* 표시 조건 — 층·최소 커버리지 */}
        <div className="fbar__item">
          <button
            type="button"
            className={`fbar__trigger${conditionCount ? ' is-on' : ''}${
              condState === 'off' ? ' is-off' : condState === 'partial' ? ' is-partial' : ''
            }`}
            aria-expanded={openDim === '_cond'}
            aria-haspopup="dialog"
            onClick={(e) => toggleAt('_cond', e.currentTarget)}
            title={
              condState === 'off'
                ? '설치 층·최소 커버리지는 이 화면에 적용되지 않습니다'
                : condState === 'partial'
                  ? partialReason(page, 'minCoverage')
                  : undefined
            }
          >
            표시 조건
            {conditionCount > 0 && <span className="fbar__badge num">{conditionCount}</span>}
            <span className="fbar__caret" aria-hidden="true">
              ▾
            </span>
          </button>

          {openDim === '_cond' && (
            <div
              className="pop pop--cond"
              role="dialog"
              aria-label="표시 조건"
              style={{ top: popTop }}
            >
              <div className="pop__field">
                <span className="pop__fieldlabel">설치 층</span>
                <div className="pop__seg">
                  {(['F', 'B'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={filters.floor === v}
                      className={`pop__segbtn${filters.floor === v ? ' is-on' : ''}`}
                      onClick={() => filters.set({ floor: filters.floor === v ? null : v })}
                    >
                      {v === 'F' ? '지상' : '지하'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pop__field">
                <label className="pop__fieldlabel" htmlFor="min-coverage">
                  최소 관측 커버리지
                  <output className="num" htmlFor="min-coverage">
                    {(filters.minCoverage * 100).toFixed(0)}%
                  </output>
                </label>
                <input
                  id="min-coverage"
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={filters.minCoverage}
                  onChange={(e) => filters.set({ minCoverage: Number(e.target.value) })}
                />
                <p className="pop__hint">미달 항목은 버리지 않고 분리 표시합니다.</p>
              </div>
            </div>
          )}
        </div>

        {activeCount > 0 && (
          <button type="button" className="btn-text fbar__reset" onClick={() => filters.reset()}>
            모두 해제
          </button>
        )}
      </div>

      {/* 적용된 필터는 항상 보인다. 다만 칩도 레일을 늘리므로 상한을 둔다 —
          접혀도 개수는 위 트리거 배지에 그대로 남는다 (§8.6.8) */}
      {applied.length > 0 && (
        <div className="fbar__applied">
          {(showAllChips ? applied : applied.slice(0, CHIP_CAP)).map((a) => (
            <button
              key={`${a.group.dim}-${a.code}`}
              type="button"
              className={`fchip${a.state === 'off' ? ' fchip--off hatch-na' : ''}`}
              onClick={() => filters.toggleList(a.group.key, a.code)}
              aria-label={
                a.state === 'off'
                  ? `${a.group.title} 필터 ${a.label} — 이 화면 미적용. 해제하려면 누르세요`
                  : `${a.group.title} 필터 ${a.label} 해제`
              }
            >
              <span className="fchip__dim">{a.group.title}</span>
              {a.label}
              {/* 지우지 않고 회색으로 남긴다 — 다른 화면에서는 살아 있는 값이다 */}
              {a.state === 'off' && <span className="fchip__off">이 화면 미적용</span>}
              <span aria-hidden="true">✕</span>
            </button>
          ))}
          {applied.length > CHIP_CAP && (
            <button
              type="button"
              className="btn-text fbar__more"
              aria-expanded={showAllChips}
              onClick={() => setShowAllChips((v) => !v)}
            >
              {showAllChips ? '접기' : `+${applied.length - CHIP_CAP}개 더`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
